// 帳票出力用の共通ヘルパー。データ集計・ルックアップ・フォーマッタを集約。
//
// 設計方針:
// - 全帳票は ExtendedDB(IDB から組み立てた db スナップショット)ドリブン。
// - 写真機能は本フェーズ対象外のため、写真関連列は「-」表記で項目だけ用意。
// - 立会(施主)/(受注)は値を備考欄からパースする想定だが、当面は「-」表記で項目だけ用意。

import ExcelJS from "exceljs";
import type { ExtendedDB } from "../../../../types/extendedDB";
import { readProp, required } from "../../../../util/typed";
import {
  deriveStirExecutions, deriveWorkExecutions, deriveQualityMeasurements,
  type DerivedStirExecution, type DerivedWorkExecution, type DerivedQualityMeasurement,
} from "../../../../types/derivedSelectors";
import Time from "../../../../util/Time";

// 行が論理削除済みか判定する
function isDeleted(row: { deleted_at?: number | null } | undefined | null): boolean {
  return row != null && row.deleted_at != null;
}

function activeRows<T extends { deleted_at?: number | null }>(rows: T[]): T[] {
  return rows.filter((r) => !isDeleted(r));
}

// =========================================================================
// ファイルダウンロード
// =========================================================================

export function downloadBlob(buffer: BlobPart, filename: string) {
  const blob = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export function todayStr(): string {
  return Time.TodayString();
}

// =========================================================================
// 共通スタイル
// =========================================================================

export const HEADER_FILL: ExcelJS.FillPattern = {
  type: "pattern", pattern: "solid", fgColor: { argb: "FFD9EDF7" },
};

export const SUBHEADER_FILL: ExcelJS.FillPattern = {
  type: "pattern", pattern: "solid", fgColor: { argb: "FFEEEEEE" },
};

export const BAD_FILL: ExcelJS.FillPattern = {
  type: "pattern", pattern: "solid", fgColor: { argb: "FFFFCDD2" },
};

export const WARN_FILL: ExcelJS.FillPattern = {
  type: "pattern", pattern: "solid", fgColor: { argb: "FFFFF59D" },
};

export function setHeaderRow(ws: ExcelJS.Worksheet, rowIdx: number = 1) {
  const r = ws.getRow(rowIdx);
  r.font = { bold: true };
  r.fill = HEADER_FILL;
  r.alignment = { vertical: "middle", horizontal: "center", wrapText: true };
  r.height = 24;
}

// =========================================================================
// ルックアップヘルパー
// =========================================================================

export type ConsumptionEntry = { constructionId: string; usedRatio: number; createdAt: number };

export type Lookups = ReturnType<typeof buildLookups>;

export function buildLookups(db: ExtendedDB) {
  const activeMaterials = activeRows(db.materials);
  const activeMaterialDetails = activeRows(db.materialDetails);
  const activeWorks = activeRows(db.works);
  const activeWorkTypes = activeRows(db.workTypes);
  const activeWorkSubtypes = activeRows(db.workSubtypes);
  const activeWorkDetails = activeRows(db.workDetails);
  const activeConstructionMethods = activeRows(db.constructionMethods);
  const activeStirringMethods = activeRows(db.stirringMethods);
  const activeCarryInLocations = activeRows(db.carryInLocations);
  const activeClients = activeRows(db.clients);
  const activeMixRatioDefinitions = activeRows(db.mixRatioDefinitions);
  const activeMixRatioDetails = activeRows(db.mixRatioDetails);
  const activeLots = activeRows(db.lots);
  const activeCarryOuts = activeRows(db.carryOuts);
  const activeCarryIns = activeRows(db.carryIns);
  const activeConstructions = activeRows(db.constructions);
  const activePlanAreas = activeRows(db.constructionPlanAreas);
  const activeLocationDivisions = activeRows(db.locationDivisions);
  const activeConstructionMaterialConsumptions = activeRows(db.constructionMaterialConsumptions);
  const activeProcessExecutions = activeRows(db.processExecutions);
  const activePotLifeSpecs = activeRows(db.potLifeSpecs);

  const materialById = new Map(activeMaterials.map((m) => [m.id, m]));
  const materialDetailById = new Map(activeMaterialDetails.map((d) => [d.id, d]));
  const materialDetailsByMaterialId = new Map<number, typeof db.materialDetails>();
  for (const d of activeMaterialDetails) {
    const arr = materialDetailsByMaterialId.get(d.material_id) ?? [];
    arr.push(d);
    materialDetailsByMaterialId.set(d.material_id, arr);
  }
  const workById = new Map(activeWorks.map((w) => [w.id, w]));
  const workTypeById = new Map(activeWorkTypes.map((t) => [t.id, t]));
  const workSubtypeById = new Map(activeWorkSubtypes.map((s) => [s.id, s]));
  const workDetailById = new Map(activeWorkDetails.map((d) => [d.id, d]));
  const constructionMethodById = new Map(activeConstructionMethods.map((c) => [c.id, c]));
  const stirringMethodById = new Map(activeStirringMethods.map((s) => [s.id, s]));
  const carryInLocationById = new Map(activeCarryInLocations.map((l) => [l.id, l]));
  const clientById = new Map(activeClients.map((c) => [c.id, c]));
  const mixRatioDefById = new Map(activeMixRatioDefinitions.map((m) => [m.id, m]));
  const mixRatioDetailsByDefId = new Map<number, typeof db.mixRatioDetails>();
  for (const md of activeMixRatioDetails) {
    const arr2 = mixRatioDetailsByDefId.get(md.mix_ratio_definition_id) ?? [];
    arr2.push(md);
    mixRatioDetailsByDefId.set(md.mix_ratio_definition_id, arr2);
  }
  const lotById = new Map(activeLots.map((l) => [l.id, l]));
  const carryOutById = new Map(activeCarryOuts.map((c) => [c.id, c]));
  const carryInById = new Map(activeCarryIns.map((c) => [c.id, c]));
  const constructionById = new Map(activeConstructions.map((c) => [c.id, c]));
  const planAreaById = new Map(activePlanAreas.map((a) => [a.id, a]));
  const locationDivBySlotAndArea = new Map<string, typeof db.locationDivisions>();
  for (const ld of activeLocationDivisions) {
    const key = `${ld.area_division_id}-${ld.slot}`;
    const arr3 = locationDivBySlotAndArea.get(key) ?? [];
    arr3.push(ld);
    locationDivBySlotAndArea.set(key, arr3);
  }
  // QR → 消費した Construction
  const constructionIdByQrId = new Map<number, string>();
  for (const cmc of activeConstructionMaterialConsumptions) {
    const qrId = consumptionQrIdOf(cmc);
    if (qrId != null) {
      constructionIdByQrId.set(qrId, cmc.construction_id);
    }
  }
  // Construction → ProcessExecutions
  const processesByConstructionId = new Map<string, typeof db.processExecutions>();
  for (const p of activeProcessExecutions) {
    const arr4 = processesByConstructionId.get(p.construction_id) ?? [];
    arr4.push(p);
    processesByConstructionId.set(p.construction_id, arr4);
  }
  // ProcessExecution → StirExecution / WorkExecution(派生)
  const stirExecutions = deriveStirExecutions({ stirBatches: activeRows(db.stirBatches) });
  const workExecutions = deriveWorkExecutions({ constructionActions: activeRows(db.constructionActions) });
  const stirByProcessId = new Map<string, DerivedStirExecution>();
  for (const s of stirExecutions) {
    for (const pid of s.process_execution_ids ?? []) {
      stirByProcessId.set(pid, s);
    }
  }
  const workExecByProcessId = new Map<string, DerivedWorkExecution>();
  for (const we of workExecutions) {
    for (const pid2 of we.process_execution_ids ?? []) {
      workExecByProcessId.set(pid2, we);
    }
  }
  // (area_id, work_detail_id) → ProcessExecution
  const processByAreaAndDetail = new Map<string, typeof db.processExecutions[number]>();
  for (const pe of activeProcessExecutions) {
    processByAreaAndDetail.set(`${pe.construction_plan_area_id}-${pe.work_detail_id}`, pe);
  }
  // Construction → 消費 QR ids
  const consumedQrIdsByConstructionId = new Map<string, number[]>();
  for (const cmc2 of activeConstructionMaterialConsumptions) {
    const consumedQrId = consumptionQrIdOf(cmc2);
    if (consumedQrId != null) {
      const arr5 = consumedQrIdsByConstructionId.get(cmc2.construction_id) ?? [];
      arr5.push(consumedQrId);
      consumedQrIdsByConstructionId.set(cmc2.construction_id, arr5);
    }
  }
  // QR部分消費モデル: QR → 消費行[](alive のみ)。1 QR が複数施工に部分消費され得るため配列。
  const consumptionsByQrId = new Map<number, ConsumptionEntry[]>();
  for (const cmc3 of activeConstructionMaterialConsumptions) {
    const qrId3 = consumptionQrIdOf(cmc3);
    if (qrId3 != null) {
      const arr8 = consumptionsByQrId.get(qrId3) ?? [];
      arr8.push({
        constructionId: cmc3.construction_id,
        usedRatio: cmc3.used_ratio ?? 1,
        createdAt: cmc3.createdAt,
      });
      consumptionsByQrId.set(qrId3, arr8);
    }
  }
  // PotLifeSpec
  const potLifeSpecsByMaterialId = new Map<number, typeof db.potLifeSpecs>();
  for (const pl of activePotLifeSpecs) {
    const arr6 = potLifeSpecsByMaterialId.get(pl.material_id) ?? [];
    arr6.push(pl);
    potLifeSpecsByMaterialId.set(pl.material_id, arr6);
  }
  // QualityMeasurements(QualityRecord+QualityMeasurementItem からの派生)
  const qualityMeasurements = deriveQualityMeasurements({
    qualityRecords: activeRows(db.qualityRecords),
    qualityMeasurementItems: activeRows(db.qualityMeasurementItems),
  });
  const qmsByConstructionPlanAreaId = new Map<string, DerivedQualityMeasurement[]>();
  for (const q of qualityMeasurements) {
    if (!q.construction_plan_area_id) continue;
    const arr7 = qmsByConstructionPlanAreaId.get(q.construction_plan_area_id) ?? [];
    arr7.push(q);
    qmsByConstructionPlanAreaId.set(q.construction_plan_area_id, arr7);
  }

  return {
    materialById, materialDetailById, materialDetailsByMaterialId,
    workById, workTypeById, workSubtypeById, workDetailById,
    constructionMethodById, stirringMethodById, carryInLocationById,
    clientById,
    mixRatioDefById, mixRatioDetailsByDefId,
    lotById, carryOutById, carryInById, constructionById, planAreaById,
    locationDivBySlotAndArea,
    constructionIdByQrId, processesByConstructionId, consumedQrIdsByConstructionId,
    consumptionsByQrId,
    stirByProcessId, workExecByProcessId, processByAreaAndDetail,
    potLifeSpecsByMaterialId, qmsByConstructionPlanAreaId,
  };
}

// =========================================================================
// 材料データ集計ヘルパー
// =========================================================================

// 材料細目の容量(kg)を合算する。content_unit が 'kg' のもののみ対象。
export function sumContentKg(details: ExtendedDB["materialDetails"]): number {
  return details.filter((d) => d.content_unit === "kg").reduce((a, d) => a + (d.content_quantity ?? 0), 0);
}

// 製造日 + 保管月数 から使用期限(YYYY-MM-DD)を算出する。算出不能時は空文字。
export function calcExpiryDate(manufacturedAt: string | undefined | null, shelfMonths: number | undefined | null): string {
  if (!manufacturedAt || shelfMonths == null) return "";
  const d = new Date(manufacturedAt);
  if (Number.isNaN(d.getTime())) return "";
  d.setMonth(d.getMonth() + shelfMonths);
  return d.toISOString().slice(0, 10);
}

// 旧キャッシュ行は construction_plan_area_id ではなく area_id を持つことがあるため両対応で解決する。
export function processAreaIdOf(process: { construction_plan_area_id: string | null } | undefined): string | undefined {
  if (process == null) {
    return undefined;
  }
  if (process.construction_plan_area_id != null) {
    return process.construction_plan_area_id;
  }
  const legacy = readProp(process, "area_id");
  if (typeof legacy === "string") {
    return legacy;
  }
  return undefined;
}

// 旧キャッシュ行は material_qr_id ではなく material_id を持つことがあるため両対応で解決する。
export function consumptionQrIdOf(consumption: { material_qr_id: number | null | undefined }): number | undefined {
  if (consumption.material_qr_id != null) {
    return consumption.material_qr_id;
  }
  const legacy = readProp(consumption, "material_id");
  if (typeof legacy === "number") {
    return legacy;
  }
  return undefined;
}

// QR 群から重複・空を除いた lot_id 一覧を取り出す。
export function uniqueLotIds(qrs: ExtendedDB["materialQrs"]): string[] {
  return Array.from(new Set(qrs.map((q) => q.lot_id).filter((id): id is string => Boolean(id))));
}

// QR の消費比率(部分消費モデル)。ConstructionMaterialConsumption の Σ を [0,1] に clamp する(StockPage.buildRatioByQrId と同じ正規化)。
export function sumAndClampRatio(entries: ConsumptionEntry[]): number {
  const total = entries.reduce((a, e) => a + e.usedRatio, 0);
  return Math.min(1, Math.max(0, total));
}

// =========================================================================
// 配合比のレシピ文字列生成
// =========================================================================

function formatRatioRange(ratioStart: number, ratioEnd: number | null | undefined): string {
  if (ratioEnd != null && ratioEnd !== ratioStart) {
    return `${ratioStart}〜${ratioEnd}`;
  }
  return String(ratioStart);
}

export function buildMixRatioRecipe(defId: number | undefined, lookups: Lookups): { recipe: string; stirringTime?: number } {
  if (defId == null) return { recipe: "" };
  const def = lookups.mixRatioDefById.get(defId);
  if (!def) return { recipe: "" };
  const details = (lookups.mixRatioDetailsByDefId.get(defId) ?? []).filter((d) => !isDeleted(d));
  const parts: string[] = [];
  for (const d of details) {
    const detail = lookups.materialDetailById.get(d.material_detail_id);
    const name = detail?.detail_name ?? "?";
    const range = formatRatioRange(d.ratio_start, d.ratio_end);
    parts.push(`${name}:${range}`);
  }
  return { recipe: parts.join(" / "), stirringTime: def.stirring_time_minutes };
}

// =========================================================================
// PotLifeSpec から温度に対応する可使時間を取得
// =========================================================================

export function findPotLifeMinutes(materialId: number, temperature: number | undefined, lookups: Lookups): number | undefined {
  const specs = lookups.potLifeSpecsByMaterialId.get(materialId);
  if (!specs || specs.length === 0) return undefined;
  if (temperature == null) {
    const s = specs.find((s) => !isDeleted(s));
    return s?.pot_life_minutes ?? undefined;
  }
  for (const spec of specs) {
    if (isDeleted(spec)) continue;
    const lo = spec.temperature_lower;
    const hi = spec.temperature_upper;
    const okLo = lo == null || temperature >= lo;
    const okHi = hi == null || temperature <= hi;
    if (okLo && okHi) return spec.pot_life_minutes ?? undefined;
  }
  return undefined;
}

// =========================================================================
// 合否判定
// =========================================================================

export function judge(value: number | undefined, lower: number | null | undefined, upper: number | null | undefined): "OK" | "NG" | "-" {
  if (value == null) return "-";
  if (lower != null && value < lower) return "NG";
  if (upper != null && value > upper) return "NG";
  return "OK";
}

export function judgeChar(value: "OK" | "NG" | "-" | undefined): string {
  if (value === "OK") return "○";
  if (value === "NG") return "×";
  return "-";
}

// =========================================================================
// 工種・種別・工程の名前解決
// =========================================================================

function lookupById<V>(map: Map<number, V>, id: number | undefined): V | undefined {
  if (id == null) {
    return undefined;
  }
  return map.get(id);
}

export function resolveWorkLabels(
  workTypeId: number | undefined,
  workSubtypeId: number | undefined,
  workDetailId: number | undefined,
  lookups: Lookups,
): { workType: string; workSubtype: string; workDetail: string } {
  const wt = lookupById(lookups.workTypeById, workTypeId);
  const wst = lookupById(lookups.workSubtypeById, workSubtypeId);
  const wd = lookupById(lookups.workDetailById, workDetailId);
  return {
    workType: wt?.name ?? "",
    workSubtype: wst?.name ?? "",
    workDetail: wd?.name ?? "",
  };
}

// =========================================================================
// QRコード文字列生成 (8桁ゼロ埋め + 接頭辞)
// =========================================================================

export function formatQrCode(qrId: number): string {
  return `0025302${qrId.toString().padStart(8, "0")}`;
}

// =========================================================================
// 期間文字列
// =========================================================================

export function fmtDate(s?: string | number | null): string {
  if (typeof s === "number") {
    if (Number.isNaN(s)) {
      return "";
    }
    return new Time(s).ToDateInput();
  }
  if (!s) {
    return "";
  }
  return s.slice(0, 10);
}

export function fmtTime(s?: string): string {
  if (!s) return "";
  if (s.includes("T")) return s.slice(11, 16);
  if (s.includes(" ")) return (s.split(" ")[1] ?? "").slice(0, 5);
  return "";
}

export function fmtDateTime(s?: string): string {
  if (!s) return "";
  return s.replace("T", " ").slice(0, 19);
}

export function diffMinutes(start?: string, end?: string): number | undefined {
  if (!start || !end) return undefined;
  const s = new Date(start).getTime();
  const e = new Date(end).getTime();
  if (isNaN(s) || isNaN(e)) return undefined;
  return Math.round((e - s) / 60000);
}

// =========================================================================
// 数値整形
// =========================================================================

export function round1(v: number | undefined | null): number | "" {
  if (v == null || isNaN(v)) return "";
  return Math.round(v * 10) / 10;
}

export function nullToDash(v: string | number | null | undefined): string | number {
  if (v == null) return "-";
  return v;
}

// =========================================================================
// 対象工事のデフォルト選択
// =========================================================================

export function selectDefaultWorkId(db: {
  works: Array<{ id: number; deleted_at: number | null }>;
  carryIns: Array<{ work_id: number | null; deleted_at?: number | null }>;
  constructionPlanAreas: Array<{ work_id: number | null; deleted_at?: number | null }>;
}): number | undefined {
  const works = db.works.filter((w) => w.deleted_at == null);
  if (works.length === 0) return undefined;
  const scores = works.map((w) => {
    const ci = db.carryIns.filter((c) => c.deleted_at == null && c.work_id === w.id).length;
    const areas = db.constructionPlanAreas.filter((a) => a.deleted_at == null && a.work_id === w.id).length;
    return { id: w.id, score: ci + areas };
  });
  scores.sort((a, b) => b.score - a.score);
  const top = scores[0];
  if (top?.score && top.score > 0) {
    return top.id;
  }
  return required(works[0], "帳票対象の工事").id;
}
